import React, { useEffect, useRef, useState } from 'react';

const EnhancedBackground = ({ currentSlide = 0 }) => {
    const canvasRef = useRef(null);
    // Phone (<768), Tablet/iPad (768–1367), Desktop (1368+): particle counts vary
    const [isPhone, setIsPhone] = useState(() =>
        typeof window !== 'undefined' && window.innerWidth < 768
    );
    const [isTablet, setIsTablet] = useState(() =>
        typeof window !== 'undefined' && window.innerWidth >= 768 && window.innerWidth < 1368
    );
    const spawnCancelledRef = useRef(false);
    const animationFrameRef = useRef(null);
    const mouseRef = useRef({ x: 0, y: 0 });
    const particlesRef = useRef([]);
    const ripplesRef = useRef([]);
    const footerTopRef = useRef(null);
    const footerRectRef = useRef(null);
    const scrollYRef = useRef(0);
    const peopleCardRectsRef = useRef([]);
    const whatWeOfferCardRectsRef = useRef([]);
    const placeholderRectRef = useRef(null); // Placeholder container rect
    const frameCountRef = useRef(0);
    const peopleObserverRef = useRef(null);
    const logoPositionRef = useRef({ x: 0, y: 0, active: false });
    const loadingLogoPositionRef = useRef({ x: 0, y: 0, active: false }); // Loading screen logo position
    const smoothedLoadingLogoPositionRef = useRef({ x: 0, y: 0 }); // Smoothed position for interpolation
    const connectionAnimationsRef = useRef(new Map()); // Track connection animations: "i-j" -> { progress, direction }
    const logoConnectionAnimationsRef = useRef(new Map()); // Track logo connection animations: "i-logo" -> { progress, direction }
    const currentSlideRef = useRef(currentSlide); // Track current slide for use in animate function
    const globalFadeInRef = useRef(0); // Global fade-in opacity for initial load (0 to 1)
    const fadeInStartTimeRef = useRef(Date.now()); // Track when fade-in started

    useEffect(() => {
        const checkViewport = () => {
            const w = window.innerWidth;
            setIsPhone(w < 768);
            setIsTablet(w >= 768 && w < 1368);
        };
        window.addEventListener('resize', checkViewport);
        return () => window.removeEventListener('resize', checkViewport);
    }, []);

    // Initialize particles (re-runs when isTablet changes so tablet gets fewer dots/lines)
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        spawnCancelledRef.current = false;
        // Phone: few dots, longer lines; tablet: fewer dots, longer lines; desktop: full
        const particleCount = isPhone ? 4 : isTablet ? 6 : 15;
        const connectionDistance = isPhone ? 120 : isTablet ? 160 : 180; // habaan lines sa phone/tablet
        const logoConnectionCount = isPhone ? 2 : isTablet ? 4 : 15;
        const speedFactor = isPhone ? 0.6 : isTablet ? 0.75 : 1; // bagal unti sa phone/tablet

        const ctx = canvas.getContext('2d');

        const updatePeopleCardRects = () => {
            const cards = document.querySelectorAll('[data-people-behind-card="true"]');
            peopleCardRectsRef.current = Array.from(cards).map((card) => {
                const rect = card.getBoundingClientRect();
                return {
                    left: rect.left,
                    right: rect.right,
                    top: rect.top,
                    bottom: rect.bottom,
                };
            });
        };
        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);

        // Get footer position and rect
        const updateFooterPosition = () => {
            const footer = document.querySelector('footer');
            if (footer) {
                const rect = footer.getBoundingClientRect();
                footerTopRef.current = rect.top + window.scrollY;
                footerRectRef.current = {
                    top: rect.top,
                    bottom: rect.bottom,
                    left: rect.left,
                    right: rect.right
                };
            } else {
                footerRectRef.current = null;
            }
        };
        updateFooterPosition();
        window.addEventListener('resize', updateFooterPosition);

        const updateWhatWeOfferCardRects = () => {
            const cards = document.querySelectorAll('[data-what-we-offer-card="true"]');
            whatWeOfferCardRectsRef.current = Array.from(cards).map((card) => {
                const rect = card.getBoundingClientRect();
                return {
                    left: rect.left,
                    right: rect.right,
                    top: rect.top,
                    bottom: rect.bottom,
                };
            });
        };

        // Get placeholder container position and rect
        const updatePlaceholderRect = () => {
            const placeholder = document.querySelector('[data-placeholder-container="true"]');
            if (placeholder) {
                const rect = placeholder.getBoundingClientRect();
                placeholderRectRef.current = {
                    left: rect.left,
                    right: rect.right,
                    top: rect.top,
                    bottom: rect.bottom,
                };
            } else {
                placeholderRectRef.current = null;
            }
        };
        
        updatePeopleCardRects();
        updateWhatWeOfferCardRects();
        updatePlaceholderRect();
        window.addEventListener('resize', updatePeopleCardRects);
        window.addEventListener('resize', updateWhatWeOfferCardRects);
        window.addEventListener('resize', updatePlaceholderRect);

        // Scroll handler to update footer position
        const handleScroll = () => {
            scrollYRef.current = window.scrollY;
            updateFooterPosition();
            updatePeopleCardRects();
            updateWhatWeOfferCardRects();
            updatePlaceholderRect();
        };
        window.addEventListener('scroll', handleScroll, { passive: true });

        // Create elegant particles (fewer on tablet to avoid lag)
        particlesRef.current = [];
        
        // Spawn particles gradually with delay - with improved bounds checking
        const spawnParticles = () => {
            // Ensure canvas dimensions are valid
            if (!canvas || canvas.width <= 0 || canvas.height <= 0) {
                console.warn('Canvas not ready for particle spawning');
                return;
            }
            
            for (let i = 0; i < particleCount; i++) {
                setTimeout(() => {
                    if (spawnCancelledRef.current) return;
                    try {
                        // Ensure canvas is still valid when spawning
                        if (!canvas || canvas.width <= 0 || canvas.height <= 0) {
                            return;
                        }
                        
                        // Spawn particles within safe bounds (larger margin to prevent going outside)
                        const margin = 80; // Increased margin from 50 to 80 for better bounds safety
                        const safeWidth = Math.max(100, canvas.width - margin * 2); // Minimum 100px width
                        const safeHeight = Math.max(100, canvas.height - margin * 2); // Minimum 100px height
                        
                        // Ensure spawn position is within safe bounds
                        const spawnX = Math.max(margin, Math.min(canvas.width - margin, margin + Math.random() * safeWidth));
                        const spawnY = Math.max(margin, Math.min(canvas.height - margin, margin + Math.random() * safeHeight));
                        
                        particlesRef.current.push({
                            x: spawnX,
                            y: spawnY,
                            vx: (Math.random() - 0.5) * 0.28 * speedFactor,
                            vy: (Math.random() - 0.5) * 0.28 * speedFactor,
                            radius: Math.random() * 6 + 6,
                            baseRadius: Math.random() * 6 + 6,
                            opacity: 0,
                            baseOpacity: Math.random() * 0.3 + 0.25,
                            targetOpacity: Math.random() * 0.3 + 0.25,
            angle: Math.random() * Math.PI * 2,
            rotationSpeed: (Math.random() - 0.5) * 0.01,
            color: `rgba(255, 150, 60, ${Math.random() * 0.3 + 0.4})`,
                            // Color transition properties for logo connection
                            isConnectedToLogo: false,
                            logoColorBlend: 0, // 0 = normal color, 1 = #F39824
                            normalColor: { r: 255, g: 150, b: 60 }, // Brighter orange color (increased from g:120, b:40)
                            logoColor: { r: 243, g: 152, b: 36 }, // #F39824 brighter orange
            pulsePhase: Math.random() * Math.PI * 2,
            pulseSpeed: Math.random() * 0.015 + 0.008,
                            spawnTime: Date.now() + i * 50,
                            isSpawning: true
                        });
                    } catch (error) {
                        console.warn('Error spawning particle:', error);
                    }
                }, i * 50);
            }
        };
        
        spawnParticles();

        const peopleSection = document.querySelector('.people-behind-section');
        if (peopleSection) {
            const observer = new MutationObserver(() => {
                updatePeopleCardRects();
            });
            observer.observe(peopleSection, { childList: true, subtree: true, attributes: true });
            peopleObserverRef.current = observer;
        }

        const whatWeOfferSection = document.querySelector('#what-we-offer');
        if (whatWeOfferSection) {
            const observer = new MutationObserver(() => {
                updateWhatWeOfferCardRects();
            });
            observer.observe(whatWeOfferSection, { childList: true, subtree: true, attributes: true });
        }

        // Mouse move handler
        const handleMouseMove = (e) => {
            mouseRef.current = { x: e.clientX, y: e.clientY };
            
            // Create subtle ripple effect
            ripplesRef.current.push({
                x: e.clientX,
                y: e.clientY,
                radius: 0,
                maxRadius: 250,
                opacity: 0.3,
                speed: 1.5,
            });

            // Limit ripples
            if (ripplesRef.current.length > 3) {
                ripplesRef.current.shift();
            }
        };

        // Mouse leave handler - fade out interactions
        const handleMouseLeave = () => {
            mouseRef.current = { x: -1000, y: -1000 };
        };

        window.addEventListener('mousemove', handleMouseMove, { passive: true });
        window.addEventListener('mouseleave', handleMouseLeave);

        // Animation loop
        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            const mouse = mouseRef.current;
            const mouseX = mouse.x;
            const mouseY = mouse.y;

            frameCountRef.current += 1;
            
            // Global fade-in effect on initial load (2 seconds fade-in)
            const fadeInDuration = 2000; // 2 seconds
            const elapsed = Date.now() - fadeInStartTimeRef.current;
            if (elapsed < fadeInDuration) {
                globalFadeInRef.current = Math.min(1, elapsed / fadeInDuration);
            } else {
                globalFadeInRef.current = 1; // Fully visible after fade-in
            }
            if (frameCountRef.current % 60 === 0) {
                updatePeopleCardRects();
                updateWhatWeOfferCardRects();
                updatePlaceholderRect();
            }

            // Get current slide value once for use throughout animate function
            const currentSlideValue = currentSlideRef.current;
            
            // Update landing page logo position EVERY FRAME for accurate tracking
            // Only track logo when currentSlide === 0 (logo is showing)
            const logoElement = document.querySelector('[data-landing-logo]');
            if (logoElement && currentSlideValue === 0) {
                try {
                    // Get the logo's bounding rect to check visibility
                    const rect = logoElement.getBoundingClientRect();
                    
                    // Check opacity of the slideshow container (parent of parent)
                    const slideshowContainer = logoElement.parentElement?.parentElement;
                    const slideshowOpacity = slideshowContainer ? parseFloat(window.getComputedStyle(slideshowContainer).opacity) : 1;
                    
                    // Logo is visible if:
                    // 1. Has valid dimensions
                    // 2. Slideshow container opacity > 0.1 (more lenient check)
                    // 3. Position values are valid
                    if (rect.width > 0 && rect.height > 0 && slideshowOpacity > 0.1 &&
                        !isNaN(rect.left) && !isNaN(rect.top) &&
                        !isNaN(rect.width) && !isNaN(rect.height)) {
                        const logoCenterX = rect.left + rect.width / 2;
                        const logoCenterY = rect.top + rect.height / 2;
                        
                        // Validate calculated position
                        if (isFinite(logoCenterX) && isFinite(logoCenterY)) {
                            // Store logo bounds for clipping connections to logo area only
                            logoPositionRef.current = { 
                                x: logoCenterX, 
                                y: logoCenterY, 
                                active: true,
                                left: rect.left,
                                right: rect.right,
                                top: rect.top,
                                bottom: rect.bottom,
                                width: rect.width,
                                height: rect.height
                            };
                        }
                    } else {
                        // Logo element exists but not visible - don't clear immediately
                        // Keep previous position for a few frames to prevent connection breaks
                        if (logoPositionRef.current.active) {
                            if (!logoPositionRef.current._fadeCount) {
                                logoPositionRef.current._fadeCount = 1;
                            } else {
                                logoPositionRef.current._fadeCount++;
                                if (logoPositionRef.current._fadeCount > 3) {
                                    logoPositionRef.current.active = false;
                                }
                            }
                        }
                    }
                } catch (error) {
                    // Silently handle errors
                    if (process.env.NODE_ENV === 'development') {
                        console.warn('Landing page logo tracking error:', error);
                    }
                }
            } else if (currentSlideValue !== 0) {
                // Not on logo slide - don't clear immediately, fade out connections
                if (logoPositionRef.current.active) {
                    if (!logoPositionRef.current._fadeCount) {
                        logoPositionRef.current._fadeCount = 1;
                    } else {
                        logoPositionRef.current._fadeCount++;
                        if (logoPositionRef.current._fadeCount > 5) {
                            logoPositionRef.current.active = false;
                        }
                    }
                }
            }

            // Update loading screen logo position EVERY FRAME for accurate tracking
            // This ensures connections stay accurate even during fast logo movement
            try {
                const loadingScreenContainer = document.querySelector('[class*="fixed inset-0 z-[9999]"]');
                if (loadingScreenContainer) {
                    // Check if loading screen container is fading out (transitioning)
                    const containerStyle = window.getComputedStyle(loadingScreenContainer);
                    const containerOpacity = parseFloat(containerStyle.opacity) || 0;
                    
                    // Only clear connections when container is completely gone (opacity = 0 or very close to 0)
                    // Keep connections active during transition (opacity > 0.1)
                    if (containerOpacity <= 0.1) {
                        loadingLogoPositionRef.current.active = false;
                        // Don't clear animations immediately - let them fade out naturally
                    } else {
                        // Check if loading screen is still active and logo exists
                        const loadingLogoElement = loadingScreenContainer.querySelector('[data-loading-logo]');
                        const loadingScreenImg = loadingScreenContainer.querySelector('img[alt="The Adox Logo"]');
                        
                        // Logo should be visible (opacity > 0) - track it even during transition
                        if (loadingLogoElement || loadingScreenImg) {
                            const logoToCheck = loadingLogoElement || loadingScreenImg?.parentElement?.parentElement;
                            if (logoToCheck) {
                                const computedStyle = window.getComputedStyle(logoToCheck);
                                const opacity = parseFloat(computedStyle.opacity) || 0;
                                
                                // Track logo position even during transition (opacity > 0.1 means it's still visible)
                                // Keep connections active as long as logo is visible (even if transitioning)
                                if (opacity > 0.1) {
                                    const rect = logoToCheck.getBoundingClientRect();
                                    // Validate rect is valid before using
                                    if (rect.width > 0 && rect.height > 0 &&
                                        !isNaN(rect.left) && !isNaN(rect.top) &&
                                        !isNaN(rect.width) && !isNaN(rect.height)) {
                                        const loadingLogoCenterX = rect.left + rect.width / 2;
                                        const loadingLogoCenterY = rect.top + rect.height / 2;
                                        
                                        // Validate calculated position
                                        if (isFinite(loadingLogoCenterX) && isFinite(loadingLogoCenterY)) {
                                            // Store target position (will be smoothly interpolated every frame)
                                            loadingLogoPositionRef.current = { x: loadingLogoCenterX, y: loadingLogoCenterY, active: true };
                                            
                                            // Initialize smoothed position if not already set (first time detection)
                                            if (smoothedLoadingLogoPositionRef.current.x === 0 && smoothedLoadingLogoPositionRef.current.y === 0) {
                                                smoothedLoadingLogoPositionRef.current.x = loadingLogoCenterX;
                                                smoothedLoadingLogoPositionRef.current.y = loadingLogoCenterY;
                                            }
                                        }
                                    }
                                } else {
                                    // Logo is fading out but still visible - keep connections but mark as inactive soon
                                    // Don't clear immediately to prevent connection breaks
                                    if (opacity <= 0.05) {
                                        loadingLogoPositionRef.current.active = false;
                                    }
                                }
                            }
                        }
                    }
                } else {
                    // Loading screen not active - only clear if we're sure it's gone
                    // Check if we still have valid position before clearing
                    if (loadingLogoPositionRef.current.active) {
                        // Give it a few frames before clearing to prevent premature disconnection
                        if (!loadingLogoPositionRef.current._clearCount) {
                            loadingLogoPositionRef.current._clearCount = 1;
                        } else {
                            loadingLogoPositionRef.current._clearCount++;
                            if (loadingLogoPositionRef.current._clearCount > 5) {
                                loadingLogoPositionRef.current.active = false;
                            }
                        }
                    }
                }
            } catch (error) {
                // Silently handle errors to prevent breaking animation
                if (process.env.NODE_ENV === 'development') {
                    console.warn('Loading screen logo tracking error:', error);
                }
            }
            
            // Smoothly interpolate loading logo position every frame for smooth connections
            // Use more responsive interpolation for better accuracy
            if (loadingLogoPositionRef.current.active) {
                const targetX = loadingLogoPositionRef.current.x;
                const targetY = loadingLogoPositionRef.current.y;
                const currentX = smoothedLoadingLogoPositionRef.current.x;
                const currentY = smoothedLoadingLogoPositionRef.current.y;
                
                // More responsive interpolation factor (0.25 = faster response to logo movement)
                const lerpFactor = 0.25;
                smoothedLoadingLogoPositionRef.current.x = currentX + (targetX - currentX) * lerpFactor;
                smoothedLoadingLogoPositionRef.current.y = currentY + (targetY - currentY) * lerpFactor;
                
                // Validate smoothed position
                if (!isFinite(smoothedLoadingLogoPositionRef.current.x) || !isFinite(smoothedLoadingLogoPositionRef.current.y)) {
                    // Reset to target if invalid
                    smoothedLoadingLogoPositionRef.current.x = targetX;
                    smoothedLoadingLogoPositionRef.current.y = targetY;
                }
            } else {
                // Don't reset immediately - let it fade out smoothly
                // Only reset if inactive for multiple frames
                if (smoothedLoadingLogoPositionRef.current.x !== 0 || smoothedLoadingLogoPositionRef.current.y !== 0) {
                    smoothedLoadingLogoPositionRef.current.x *= 0.95; // Fade out slowly
                    smoothedLoadingLogoPositionRef.current.y *= 0.95;
                    if (Math.abs(smoothedLoadingLogoPositionRef.current.x) < 0.1 && Math.abs(smoothedLoadingLogoPositionRef.current.y) < 0.1) {
                        smoothedLoadingLogoPositionRef.current.x = 0;
                        smoothedLoadingLogoPositionRef.current.y = 0;
                    }
                }
            }
            
            // Loading screen logo tracking is already done above (every frame)
            // No duplicate code needed here

            // Update and draw particles
            const scrollY = scrollYRef.current;
            const footerTop = footerTopRef.current;
            const viewportHeight = window.innerHeight;
            const viewportBottom = scrollY + viewportHeight;
            
            particlesRef.current.forEach((particle, index) => {
                // Handle particle spawning - fade in gradually
                if (particle.isSpawning && Date.now() >= particle.spawnTime) {
                    particle.opacity += (particle.targetOpacity - particle.opacity) * 0.05;
                    if (Math.abs(particle.opacity - particle.targetOpacity) < 0.01) {
                        particle.opacity = particle.targetOpacity;
                        particle.baseOpacity = particle.targetOpacity;
                        particle.isSpawning = false;
                    }
                }
                
                particle.isHidden = false;
                // Check if particle is in footer area - hide completely if so
                // Since canvas is fixed, particles are in viewport coordinates
                const footerRect = footerRectRef.current;
                let isInFooter = false;
                
                if (footerRect) {
                    // Check if particle is within footer bounds
                    isInFooter = particle.x >= footerRect.left && 
                                 particle.x <= footerRect.right &&
                                 particle.y >= footerRect.top && 
                                 particle.y <= footerRect.bottom;
                } else {
                    // Fallback: check if near footer using distance
                    const distanceToFooter = footerTop !== null ? footerTop - viewportBottom : Infinity;
                    const isNearFooter = distanceToFooter < 500;
                    const particleInBottomViewport = particle.y > viewportHeight * 0.5;
                    isInFooter = isNearFooter && particleInBottomViewport;
                }
                
                if (isInFooter) {
                    // Completely hide particle in footer
                    particle.isHidden = true;
                    particle.opacity = 0;
                    return; // Skip drawing completely
                } else {
                    // Restore opacity if not in footer
                    if (particle.opacity < particle.baseOpacity) {
                        particle.opacity = Math.min(particle.baseOpacity, particle.opacity + 0.08);
                    }
                }
                
                // Hide particles that are too close to logo (behind/near logo)
                // Check both loading screen logo and landing page logo (when showing)
                // Note: Particles behind placeholder are naturally hidden by z-index layering
                const currentLoadingScreenContainer = document.querySelector('[class*="fixed inset-0 z-[9999]"]');
                const isOnLoadingScreen = currentLoadingScreenContainer !== null;
                const currentSlideValue = currentSlideRef.current;
                const isLandingPageLogoShowing = logoPositionRef.current.active && !isOnLoadingScreen && currentSlideValue === 0;
                
                // Check if any logo is active
                const isAnyLogoActive = loadingLogoPositionRef.current.active || isLandingPageLogoShowing;
                
                if (isAnyLogoActive) {
                    let logoX, logoY;
                    if (loadingLogoPositionRef.current.active) {
                        logoX = loadingLogoPositionRef.current.x;
                        logoY = loadingLogoPositionRef.current.y;
                    } else if (isLandingPageLogoShowing) {
                        logoX = logoPositionRef.current.x;
                        logoY = logoPositionRef.current.y;
                    }
                    
                    if (logoX !== undefined && logoY !== undefined) {
                        const dxLogo = logoX - particle.x;
                        const dyLogo = logoY - particle.y;
                        const distanceToLogo = Math.sqrt(dxLogo * dxLogo + dyLogo * dyLogo);
                        const minDistanceFromLogo = 120; // Minimum distance from logo - particles closer than this will be hidden
                        
                        if (distanceToLogo < minDistanceFromLogo) {
                            // Particle is too close to logo - hide it
                            particle.isHidden = true;
                            particle.opacity = 0;
                            return; // Skip drawing completely
                        } else {
                            // Particle is far enough from logo - make sure it's not hidden
                            if (particle.isHidden && particle.opacity === 0) {
                                particle.isHidden = false;
                                particle.opacity = particle.baseOpacity;
                            }
                        }
                    }
                } else {
                    // No logo is active - restore particles that were hidden
                    if (particle.isHidden && particle.opacity === 0) {
                        particle.isHidden = false;
                        particle.opacity = particle.baseOpacity;
                    }
                }
                
                // Calculate distance to mouse
                const dxMouse = mouseX - particle.x;
                const dyMouse = mouseY - particle.y;
                const distanceMouse = Math.sqrt(dxMouse * dxMouse + dyMouse * dyMouse);
                
                // Calculate distance to logo
                let distanceLogo = Infinity;
                let dxLogo = 0;
                let dyLogo = 0;
                if (logoPositionRef.current.active) {
                    dxLogo = logoPositionRef.current.x - particle.x;
                    dyLogo = logoPositionRef.current.y - particle.y;
                    distanceLogo = Math.sqrt(dxLogo * dxLogo + dyLogo * dyLogo);
                }
                
                const maxDistance = 25; // Very tight interaction range - only when cursor is extremely close
                
                // Check interaction with mouse or logo (use the closer one)
                const minDistance = Math.min(distanceMouse, distanceLogo);
                const isMouseCloser = distanceMouse < distanceLogo;
                const dx = isMouseCloser ? dxMouse : dxLogo;
                const dy = isMouseCloser ? dyMouse : dyLogo;
                const distance = minDistance;

                // Track if particle is interacting
                const isInteracting = distance < maxDistance && distance > 0;
                particle.isInteracting = isInteracting;

                // Mouse/Logo interaction - subtle repulsion and attraction
                if (isInteracting) {
                    const force = (maxDistance - distance) / maxDistance;
                    const angle = Math.atan2(dy, dx);
                    
                    // Stronger repulsion for more noticeable effect in very small range
                    const repulsionStrength = force * 0.25;
                    particle.vx -= Math.cos(angle) * repulsionStrength;
                    particle.vy -= Math.sin(angle) * repulsionStrength;

                    // More visible scale and glow effect near cursor/logo
                    const scaleFactor = 1 + force * 0.4; // Stronger glow effect for tight range
                    particle.radius = particle.baseRadius * scaleFactor;
                    particle.opacity = Math.min(particle.baseOpacity + force * 0.3, 0.6); // Higher opacity for better visibility
                } else {
                    // Return to base state smoothly
                    particle.radius += (particle.baseRadius - particle.radius) * 0.1;
                    particle.opacity += (particle.baseOpacity - particle.opacity) * 0.1;
                    particle.isInteracting = false;
                }

                // Update position
                particle.x += particle.vx;
                particle.y += particle.vy;

                // Boundary wrapping with improved safety checks - ensure particles stay within canvas
                if (!canvas || canvas.width <= 0 || canvas.height <= 0) {
                    return; // Skip if canvas is invalid
                }
                
                // Clamp particles to canvas bounds (with larger margin to prevent going outside)
                const margin = 50; // Increased margin from 10 to 50 for better bounds control
                const minX = margin;
                const maxX = canvas.width - margin;
                const minY = margin;
                const maxY = canvas.height - margin;
                
                // Wrap particles that go outside bounds (with margin)
                if (particle.x < minX) {
                    particle.x = maxX;
                } else if (particle.x > maxX) {
                    particle.x = minX;
                }
                if (particle.y < minY) {
                    particle.y = maxY;
                } else if (particle.y > maxY) {
                    particle.y = minY;
                }
                
                // Final safety clamp: ensure particles are always within safe bounds
                particle.x = Math.max(minX, Math.min(maxX, particle.x));
                particle.y = Math.max(minY, Math.min(maxY, particle.y));

                // When NOT interacting: keep dots moving; slower on phone/tablet (speedFactor)
                if (distance >= maxDistance) {
                    particle.vx *= 0.998;
                    particle.vy *= 0.998;
                    particle.vx += (Math.random() - 0.5) * 0.011 * speedFactor;
                    particle.vy += (Math.random() - 0.5) * 0.011 * speedFactor;
                    const speed = Math.sqrt(particle.vx * particle.vx + particle.vy * particle.vy);
                    const minSpeed = 0.026 * speedFactor;
                    if (speed < minSpeed && speed > 0) {
                        particle.vx *= minSpeed / speed;
                        particle.vy *= minSpeed / speed;
                    }
                } else {
                    // When interacting with mouse: keep current damping
                    particle.vx *= 0.98;
                    particle.vy *= 0.98;
                }

                // Hide particles when hovering over People Behind cards
                const isOverPeopleCard = peopleCardRectsRef.current.some((rect) => {
                    return (
                        particle.x >= rect.left &&
                        particle.x <= rect.right &&
                        particle.y >= rect.top &&
                        particle.y <= rect.bottom
                    );
                });

                if (isOverPeopleCard) {
                    particle.isHidden = true;
                    return;
                }

                // Reduce opacity when particles are inside What We Offer cards
                const isOverWhatWeOfferCard = whatWeOfferCardRectsRef.current.some((rect) => {
                    return (
                        particle.x >= rect.left &&
                        particle.x <= rect.right &&
                        particle.y >= rect.top &&
                        particle.y <= rect.bottom
                    );
                });

                if (isOverWhatWeOfferCard) {
                    // Reduce opacity to 30% of base opacity
                    particle.opacity = particle.baseOpacity * 0.3;
                } else {
                    // Restore opacity if not in What We Offer card
                    if (particle.opacity < particle.baseOpacity) {
                        particle.opacity = Math.min(particle.baseOpacity, particle.opacity + 0.05);
                    }
                }

                // Update rotation
                particle.angle += particle.rotationSpeed;

                // Subtle pulse animation
                particle.pulsePhase += particle.pulseSpeed;
                const pulseFactor = 1 + Math.sin(particle.pulsePhase) * 0.05;
                const currentRadius = particle.radius * pulseFactor;
                
                // Store current radius for trail
                particle.currentRadius = currentRadius;

                // Skip drawing if particle is hidden (in footer)
                if (particle.opacity <= 0) {
                    return;
                }
                
                // Update color blend based on logo connection status (smooth fade)
                if (particle.isConnectedToLogo) {
                    // Fade towards logo color (#F39824) - MUCH faster fade in for immediate effect
                    particle.logoColorBlend = Math.min(1, particle.logoColorBlend + 0.15); // Much faster fade in
                } else {
                    // Fade back to normal color - slower fade out for smooth transition
                    particle.logoColorBlend = Math.max(0, particle.logoColorBlend - 0.04); // Smooth fade out
                }
                
                // Calculate current color based on blend
                const currentR = Math.round(particle.normalColor.r + (particle.logoColor.r - particle.normalColor.r) * particle.logoColorBlend);
                const currentG = Math.round(particle.normalColor.g + (particle.logoColor.g - particle.normalColor.g) * particle.logoColorBlend);
                const currentB = Math.round(particle.normalColor.b + (particle.logoColor.b - particle.normalColor.b) * particle.logoColorBlend);
                
                // Draw particle as neuron node (cell body) - more prominent and neuron-like
                ctx.save();
                ctx.translate(particle.x, particle.y);
                
                const interactionFactor = distance < maxDistance ? 1 - distance / maxDistance : 0;
                
                // Neuron cell body - larger, more prominent
                const neuronBodyRadius = currentRadius * 1.2; // Larger cell body
                const outerGlowRadius = neuronBodyRadius * 2.5; // Larger glow for neuron effect
                
                // Outer glow (neuron membrane glow) - uses current color, brighter when connected to logo
                const glowIntensity = particle.isConnectedToLogo ? 1.3 : 1.0; // Brighter glow when connected
                const glowGradient = ctx.createRadialGradient(0, 0, 0, 0, 0, outerGlowRadius);
                glowGradient.addColorStop(0, `rgba(${currentR + 80}, ${currentG + 80}, ${currentB + 110}, ${particle.opacity * 0.7 * glowIntensity})`); // Increased from 0.4 to 0.7
                glowGradient.addColorStop(0.4, `rgba(${currentR + 40}, ${currentG + 40}, ${currentB + 50}, ${particle.opacity * 0.4 * glowIntensity})`); // Increased from 0.2 to 0.4
                glowGradient.addColorStop(1, `rgba(${currentR}, ${currentG}, ${currentB}, 0)`);
                
                ctx.fillStyle = glowGradient;
                ctx.beginPath();
                ctx.arc(0, 0, outerGlowRadius, 0, Math.PI * 2);
                ctx.shadowBlur = interactionFactor > 0 ? 40 : (particle.isConnectedToLogo ? 25 : 20); // Brighter shadow when connected
                ctx.shadowColor = `rgba(${currentR + 60}, ${currentG + 60}, ${currentB + 80}, ${(0.6 + interactionFactor * 0.4) * glowIntensity})`;
                ctx.fill();
                
                // Neuron cell body (main node) - uses current color, brighter when connected to logo
                const bodyIntensity = particle.isConnectedToLogo ? 1.2 : 1.0; // Brighter body when connected
                const cellBodyGradient = ctx.createRadialGradient(0, 0, 0, 0, 0, neuronBodyRadius);
                cellBodyGradient.addColorStop(0, `rgba(${currentR + 120}, ${currentG + 120}, ${currentB + 180}, ${(0.95 + interactionFactor * 0.05) * bodyIntensity})`); // Increased from 0.9 to 0.95
                cellBodyGradient.addColorStop(0.3, `rgba(${currentR + 80}, ${currentG + 80}, ${currentB + 110}, ${particle.opacity * 1.0 * bodyIntensity})`); // Increased from 0.8 to 1.0
                cellBodyGradient.addColorStop(0.7, `rgba(${currentR + 40}, ${currentG + 40}, ${currentB + 60}, ${particle.opacity * 0.85 * bodyIntensity})`); // Increased from 0.6 to 0.85
                cellBodyGradient.addColorStop(1, `rgba(${currentR}, ${currentG}, ${currentB}, ${particle.opacity * 0.7 * bodyIntensity})`); // Increased from 0.4 to 0.7
                
                ctx.fillStyle = cellBodyGradient;
                ctx.beginPath();
                ctx.arc(0, 0, neuronBodyRadius, 0, Math.PI * 2);
                ctx.shadowBlur = particle.isConnectedToLogo ? 20 : 15; // Brighter shadow when connected
                ctx.shadowColor = `rgba(${currentR + 80}, ${currentG + 80}, ${currentB + 110}, ${(0.7 + interactionFactor * 0.3) * bodyIntensity})`;
                ctx.fill();
                
                // Neuron nucleus (bright core) - uses current color, brighter when connected to logo
                const nucleusRadius = neuronBodyRadius * 0.4;
                const nucleusIntensity = particle.isConnectedToLogo ? 1.1 : 1.0;
                ctx.fillStyle = `rgba(255, 255, 255, ${(0.95 + interactionFactor * 0.05) * nucleusIntensity})`;
                ctx.beginPath();
                ctx.arc(0, 0, nucleusRadius, 0, Math.PI * 2);
                ctx.shadowBlur = particle.isConnectedToLogo ? 15 : 10; // Brighter shadow when connected
                ctx.shadowColor = `rgba(${currentR + 120}, ${currentG + 120}, ${currentB + 160}, ${0.8 * nucleusIntensity})`;
                ctx.fill();
                ctx.shadowBlur = 0;
                
                ctx.restore();
            });

            // Update and draw ripples
            ripplesRef.current = ripplesRef.current.filter((ripple) => {
                ripple.radius += ripple.speed;
                ripple.opacity -= 0.02;

                if (ripple.opacity > 0 && ripple.radius < ripple.maxRadius) {
                    // Draw subtle ripple
                    ctx.beginPath();
                    ctx.arc(ripple.x, ripple.y, ripple.radius, 0, Math.PI * 2);
                    ctx.strokeStyle = `rgba(255, 120, 40, ${ripple.opacity * 0.15})`;
                    ctx.lineWidth = 1.5;
                    ctx.stroke();
                    
                    // Subtle inner glow
                    ctx.beginPath();
                    ctx.arc(ripple.x, ripple.y, ripple.radius * 0.7, 0, Math.PI * 2);
                    ctx.strokeStyle = `rgba(255, 120, 40, ${ripple.opacity * 0.1})`;
                    ctx.lineWidth = 1;
                    ctx.stroke();
                    
                    return true;
                }
                return false;
            });

            // Draw subtle gradient mesh that follows cursor - reduced size
            if (mouseX > 0 && mouseY > 0) {
                const meshGradient = ctx.createRadialGradient(
                    mouseX, mouseY, 0,
                    mouseX, mouseY, 150 // Reduced from 400 to 150 for smaller ambient light
                );
                meshGradient.addColorStop(0, 'rgba(255, 120, 40, 0.02)'); // Reduced opacity
                meshGradient.addColorStop(0.5, 'rgba(255, 120, 40, 0.01)'); // Reduced opacity
                meshGradient.addColorStop(1, 'rgba(255, 120, 40, 0)');
                
                ctx.fillStyle = meshGradient;
                ctx.fillRect(0, 0, canvas.width, canvas.height);
            }

            // Draw connecting lines between nearby particles with traveling glow animation
            // Add bounds checking to prevent drawing outside canvas
            if (!canvas || canvas.width <= 0 || canvas.height <= 0) {
                return; // Skip if canvas is invalid
            }
            
            const currentConnections = new Set();
            particlesRef.current.forEach((particle, i) => {
                // Validate particle exists and has valid position
                if (!particle || typeof particle.x !== 'number' || typeof particle.y !== 'number' || 
                    isNaN(particle.x) || isNaN(particle.y)) {
                    return;
                }
                
                particlesRef.current.slice(i + 1).forEach((otherParticle, j) => {
                    // Validate other particle exists and has valid position
                    if (!otherParticle || typeof otherParticle.x !== 'number' || typeof otherParticle.y !== 'number' ||
                        isNaN(otherParticle.x) || isNaN(otherParticle.y)) {
                        return;
                    }
                    
                    const dx = otherParticle.x - particle.x;
                    const dy = otherParticle.y - particle.y;
                    const distance = Math.sqrt(dx * dx + dy * dy);
                    
                    // Validate distance is a valid number
                    if (isNaN(distance) || !isFinite(distance)) {
                        return;
                    }
                    
                    // connectionDistance: smaller on tablet = fewer lines
                    if (distance < connectionDistance) {
                        // Skip if either particle is hidden
                        if (particle.isHidden || otherParticle.isHidden) {
                            return;
                        }
                        
                        // Check if both particles are within canvas bounds (with margin)
                        const margin = 50;
                        const particleInBounds = particle.x >= -margin && particle.x <= canvas.width + margin &&
                                                particle.y >= -margin && particle.y <= canvas.height + margin;
                        const otherParticleInBounds = otherParticle.x >= -margin && otherParticle.x <= canvas.width + margin &&
                                                     otherParticle.y >= -margin && otherParticle.y <= canvas.height + margin;
                        
                        // Only draw connection if both particles are within bounds
                        if (!particleInBounds || !otherParticleInBounds) {
                            return;
                        }
                        
                        // Check if line is in footer area - check if either endpoint or midpoint is in footer
                        const footerRect = footerRectRef.current;
                        // Calculate line midpoint (used for both footer and placeholder checks)
                        const lineMidpointX = (particle.x + otherParticle.x) / 2;
                        const lineMidpointY = (particle.y + otherParticle.y) / 2;
                        
                        let isLineInFooter = false;
                        
                        if (footerRect) {
                            // Check if either particle, other particle, or line midpoint is within footer bounds
                            const particleInFooter = particle.x >= footerRect.left && 
                                                    particle.x <= footerRect.right &&
                                                    particle.y >= footerRect.top && 
                                                    particle.y <= footerRect.bottom;
                            
                            const otherParticleInFooter = otherParticle.x >= footerRect.left && 
                                                          otherParticle.x <= footerRect.right &&
                                                          otherParticle.y >= footerRect.top && 
                                                          otherParticle.y <= footerRect.bottom;
                            
                            const midpointInFooter = lineMidpointX >= footerRect.left && 
                                                     lineMidpointX <= footerRect.right &&
                                                     lineMidpointY >= footerRect.top && 
                                                     lineMidpointY <= footerRect.bottom;
                            
                            isLineInFooter = particleInFooter || otherParticleInFooter || midpointInFooter;
                        } else {
                            // Fallback: check if near footer using distance
                            const distanceToFooter = footerTop !== null ? footerTop - viewportBottom : Infinity;
                            const isNearFooter = distanceToFooter < 500;
                            const lineInBottomViewport = lineMidpointY > viewportHeight * 0.5 || 
                                                         particle.y > viewportHeight * 0.5 || 
                                                         otherParticle.y > viewportHeight * 0.5;
                            isLineInFooter = isNearFooter && lineInBottomViewport;
                        }
                        
                        // Skip drawing line if it's in footer area - completely hide
                        if (isLineInFooter) {
                            return;
                        }
                        
                        // Note: Lines behind placeholder are naturally hidden by z-index layering
                        // No need to hide them explicitly - placeholder has higher z-index
                        
                        // Create connection key
                        const connectionKey = `${i}-${i + 1 + j}`;
                        currentConnections.add(connectionKey);
                        
                        // Check if this is a new connection
                        const isNewConnection = !connectionAnimationsRef.current.has(connectionKey);
                        
                        // Check if either particle is interacting
                        const hasInteraction = (particle.isInteracting || otherParticle.isInteracting);
                        
                        // Initialize or get animation state for this connection
                        if (isNewConnection) {
                            connectionAnimationsRef.current.set(connectionKey, {
                                progress: 0,
                                direction: 1, // 1 for particle -> otherParticle, -1 for reverse
                                speed: 0.004 + Math.random() * 0.002, // Much slower speed (0.004-0.006)
                                frameCreated: frameCountRef.current,
                                isNew: true,
                                lastInteractionFrame: hasInteraction ? frameCountRef.current : 0
                            });
                        }
                        
                        const animState = connectionAnimationsRef.current.get(connectionKey);
                        
                        // Update last interaction frame to extend visibility during interactions
                        if (hasInteraction) {
                            animState.lastInteractionFrame = frameCountRef.current;
                        }
                        
                        // Update speed for existing connections if they're interacting
                        if (!isNewConnection) {
                            if (hasInteraction && animState.speed < 0.006) {
                                animState.speed = 0.006 + Math.random() * 0.002; // Slightly faster during interaction (0.006-0.008)
                            } else if (!hasInteraction && animState.speed > 0.005) {
                                animState.speed = 0.003 + Math.random() * 0.002; // Very slow normal speed (0.003-0.005)
                            }
                        }
                        
                        const framesSinceCreation = frameCountRef.current - animState.frameCreated;
                        const framesSinceInteraction = frameCountRef.current - animState.lastInteractionFrame;
                        const newConnectionDuration = 300; // Show glow for ~5 seconds (300 frames at 60fps) - longer duration
                        const interactionExtensionDuration = 180; // Extend visibility for 3 seconds after interaction
                        const isNewlyFormed = framesSinceCreation < newConnectionDuration;
                        const isRecentlyInteracted = framesSinceInteraction < interactionExtensionDuration;
                        
                        // Only animate glow if connection is new OR particles are interacting OR recently interacted
                        const shouldAnimateGlow = isNewlyFormed || hasInteraction || isRecentlyInteracted;
                        
                        if (shouldAnimateGlow) {
                            // Update animation progress
                            animState.progress += animState.speed * animState.direction;
                            
                            // Reverse direction when reaching ends - ensure it reaches exactly 1 before reversing
                            if (animState.progress >= 1) {
                                animState.progress = 1; // Ensure it reaches the end dot
                                animState.direction = -1;
                            } else if (animState.progress <= 0) {
                                animState.progress = 0; // Ensure it reaches the start dot
                                animState.direction = 1;
                            }
                            
                            // Don't reset progress if connection is no longer new and not interacting
                            // Let it complete its cycle naturally
                        } else {
                            // Reset progress when not animating
                            animState.progress = 0;
                        }
                        
                        // Mark as no longer new after duration
                        if (isNewlyFormed && framesSinceCreation >= newConnectionDuration) {
                            animState.isNew = false;
                        }
                        
                        // Neuron axon/dendrite connection - more visible but balanced
                        // Keep normal color for particle-to-particle connections (only logo connections get bright color)
                        const baseOpacity = (1 - distance / connectionDistance) * 1.5;
                        
                        // Apply global fade-in effect to connection lines
                        const lineFadeInOpacity = baseOpacity * globalFadeInRef.current;
                        
                        // Create neural axon gradient - more visible but balanced
                        const normalR = 255, normalG = 230, normalB = 190; // Slightly brighter colors for better visibility
                        const axonGradient = ctx.createLinearGradient(
                            particle.x, particle.y,
                            otherParticle.x, otherParticle.y
                        );
                        axonGradient.addColorStop(0, `rgba(${normalR}, ${normalG}, ${normalB}, ${lineFadeInOpacity * 1.1})`); // More visible
                        axonGradient.addColorStop(0.5, `rgba(${normalR - 5}, ${normalG - 5}, ${normalB - 5}, ${lineFadeInOpacity * 1.3})`); // Brighter center
                        axonGradient.addColorStop(1, `rgba(${normalR}, ${normalG}, ${normalB}, ${lineFadeInOpacity * 1.1})`); // More visible
                        
                        // Draw neural axon (connection line) - more visible but balanced
                        ctx.beginPath();
                        ctx.moveTo(particle.x, particle.y);
                        ctx.lineTo(otherParticle.x, otherParticle.y);
                        ctx.strokeStyle = axonGradient;
                        ctx.lineWidth = 2.2; // Slightly thicker for better visibility (balanced)
                        ctx.globalAlpha = globalFadeInRef.current; // Apply fade-in to global alpha
                        ctx.shadowBlur = 22; // Slightly brighter glow for better visibility
                        ctx.shadowColor = `rgba(${normalR}, ${normalG}, ${normalB}, ${lineFadeInOpacity * 1.1})`; // More visible shadow
                        ctx.stroke();
                        ctx.shadowBlur = 0;
                        ctx.globalAlpha = 1; // Reset global alpha
                        
                        // Draw traveling glow effect along the line (only when should animate)
                        if (shouldAnimateGlow && animState.progress >= 0) {
                            const glowProgress = animState.progress;
                            const glowX = particle.x + (otherParticle.x - particle.x) * glowProgress;
                            const glowY = particle.y + (otherParticle.y - particle.y) * glowProgress;
                            
                            // Calculate glow intensity - visible throughout, stronger in the middle
                            // Use a smoother curve that's visible at both ends (0 and 1)
                            const glowIntensity = Math.sin(glowProgress * Math.PI); // 0 at ends, 1 at middle
                            // Add minimum visibility at ends so it's always visible
                            const minIntensity = 0.7; // Increased from 0.4 to 0.7 for brighter visibility at ends
                            const adjustedIntensity = minIntensity + (1 - minIntensity) * glowIntensity;
                            
                            // Fade out for new connections as they age - only fade in the last 30% of duration
                            // BUT: Never fade during interactions or recent interactions - always full brightness
                            let fadeFactor = 1;
                            if (hasInteraction || isRecentlyInteracted) {
                                // During interactions or recently interacted: always full brightness, no fade
                                fadeFactor = 1;
                            } else if (isNewlyFormed) {
                                // Only fade if it's a new connection AND not interacting/recently interacted
                                const fadeStart = newConnectionDuration * 0.7; // Start fading at 70% of duration
                                if (framesSinceCreation > fadeStart) {
                                    const fadeProgress = (framesSinceCreation - fadeStart) / (newConnectionDuration - fadeStart);
                                    fadeFactor = Math.max(0.6, 1 - fadeProgress * 0.4); // Fade from 100% to 60% in last 30%
                                } else {
                                    fadeFactor = 1; // Full brightness for first 70%
                                }
                            } else {
                                // For old connections without interaction: still visible but slightly dimmer
                                fadeFactor = 0.8;
                            }
                            
                            // Neural network-style animation: Action potential traveling along the connection
                            // Apply global fade-in to impulses
                            const impulseOpacity = baseOpacity * 3.5 * adjustedIntensity * fadeFactor * globalFadeInRef.current; // Apply fade-in
                            const impulseSize = 4 + adjustedIntensity * 3; // Increased from 3-5 to 4-7 for larger, more visible impulse
                            
                            // Main neural impulse (bright core like action potential)
                            const coreGradient = ctx.createRadialGradient(
                                glowX, glowY, 0,
                                glowX, glowY, impulseSize
                            );
                            coreGradient.addColorStop(0, `rgba(255, 255, 240, ${impulseOpacity * 1.2})`); // Apply fade-in
                            coreGradient.addColorStop(0.3, `rgba(255, 240, 200, ${impulseOpacity * 1.1})`); // Apply fade-in
                            coreGradient.addColorStop(0.6, `rgba(255, 220, 160, ${impulseOpacity * 0.9})`); // Apply fade-in
                            coreGradient.addColorStop(1, 'rgba(255, 200, 120, 0)'); // Brighter end color
                            
                            ctx.fillStyle = coreGradient;
                            ctx.beginPath();
                            ctx.arc(glowX, glowY, impulseSize, 0, Math.PI * 2);
                            ctx.fill();
                            
                            // Bright white-hot core (like electrical charge in neuron)
                            ctx.fillStyle = `rgba(255, 255, 255, ${impulseOpacity * 1.0})`; // Apply fade-in
                            ctx.beginPath();
                            ctx.arc(glowX, glowY, impulseSize * 0.4, 0, Math.PI * 2); // Increased from 0.35 to 0.4
                            ctx.shadowBlur = 20; // Increased from 12 to 20 for brighter glow
                            ctx.shadowColor = `rgba(255, 240, 200, ${impulseOpacity * 1.2})`; // Apply fade-in
                            ctx.fill();
                            ctx.shadowBlur = 0;
                            
                            // Draw trailing effect behind the impulse (like depolarization wave)
                            if (glowProgress > 0.1) {
                                const trailProgress = Math.max(0, glowProgress - 0.15);
                                const trailX = particle.x + (otherParticle.x - particle.x) * trailProgress;
                                const trailY = particle.y + (otherParticle.y - particle.y) * trailProgress;
                                
                                const trailOpacity = baseOpacity * 1.8 * fadeFactor * globalFadeInRef.current * (1 - (glowProgress - trailProgress) / 0.15); // Apply fade-in
                                const trailGradient = ctx.createRadialGradient(
                                    trailX, trailY, 0,
                                    trailX, trailY, impulseSize * 0.8 // Increased from 0.7 to 0.8
                                );
                                trailGradient.addColorStop(0, `rgba(255, 220, 160, ${trailOpacity * 0.8})`); // Increased from 0.5 to 0.8 and brighter color
                                trailGradient.addColorStop(1, 'rgba(255, 200, 120, 0)'); // Brighter end color
                                
                                ctx.fillStyle = trailGradient;
                                ctx.beginPath();
                                ctx.arc(trailX, trailY, impulseSize * 0.8, 0, Math.PI * 2); // Increased from 0.7 to 0.8
                                ctx.fill();
                            }
                            
                            // Draw small secondary pulses (like synaptic vesicles)
                            if (glowProgress > 0.3 && glowProgress < 0.9) {
                                const pulseOffset = (glowProgress - 0.3) * 0.6;
                                const pulse1Progress = 0.3 + pulseOffset * 0.3;
                                const pulse2Progress = 0.3 + pulseOffset * 0.6;
                                
                                [pulse1Progress, pulse2Progress].forEach((pulseProg) => {
                                    if (pulseProg < glowProgress) {
                                        const pulseX = particle.x + (otherParticle.x - particle.x) * pulseProg;
                                        const pulseY = particle.y + (otherParticle.y - particle.y) * pulseProg;
                                        
                                        const pulseOpacity = baseOpacity * 1.5 * fadeFactor * globalFadeInRef.current * (1 - Math.abs(pulseProg - glowProgress) * 2); // Apply fade-in
                                        if (pulseOpacity > 0.15) { // Increased threshold from 0.1 to 0.15
                                            const pulseGradient = ctx.createRadialGradient(
                                                pulseX, pulseY, 0,
                                                pulseX, pulseY, 3 // Increased from 2 to 3 for larger pulses
                                            );
                                            pulseGradient.addColorStop(0, `rgba(255, 240, 200, ${pulseOpacity * 1.2})`); // Brighter color and increased opacity
                                            pulseGradient.addColorStop(1, 'rgba(255, 220, 160, 0)'); // Brighter end color
                                            
                                            ctx.fillStyle = pulseGradient;
                                            ctx.beginPath();
                                            ctx.arc(pulseX, pulseY, 3, 0, Math.PI * 2); // Increased from 2 to 3
                                            ctx.fill();
                                        }
                                    }
                                });
                            }
                        }
                    }
                });
            });
            
            // Clean up connections that no longer exist
            connectionAnimationsRef.current.forEach((animState, key) => {
                if (!currentConnections.has(key)) {
                    connectionAnimationsRef.current.delete(key);
                }
            });

            // Draw neural network connections from particles to logo
            // Connect to loading screen logo during loading screen
            // Connect to landing page logo when currentSlide === 0 (logo is showing)
            const currentLoadingScreenContainer = document.querySelector('[class*="fixed inset-0 z-[9999]"]');
            const isOnLoadingScreen = currentLoadingScreenContainer !== null;
            // Check if landing page logo is showing
            // Must be: active, not on loading screen, and currentSlide === 0
            const isLandingPageLogoShowing = logoPositionRef.current.active && 
                                             !isOnLoadingScreen && 
                                             currentSlideValue === 0 &&
                                             logoPositionRef.current.x !== undefined &&
                                             logoPositionRef.current.y !== undefined;
            
            // Determine which logo to connect to
            let targetLogoX = null;
            let targetLogoY = null;
            let shouldConnectToLogo = false;
            
            if (isOnLoadingScreen && loadingLogoPositionRef.current.active) {
                // Connect to loading screen logo - use smoothed position for smooth connections
                const smoothedX = smoothedLoadingLogoPositionRef.current.x;
                const smoothedY = smoothedLoadingLogoPositionRef.current.y;
                
                // Validate smoothed position before using (fallback to target if invalid)
                if (isNaN(smoothedX) || isNaN(smoothedY) || !isFinite(smoothedX) || !isFinite(smoothedY) || 
                    (smoothedX === 0 && smoothedY === 0)) {
                    // Fallback to target position if smoothed is invalid
                    const targetX = loadingLogoPositionRef.current.x;
                    const targetY = loadingLogoPositionRef.current.y;
                    if (isFinite(targetX) && isFinite(targetY) && targetX !== 0 && targetY !== 0) {
                        targetLogoX = targetX;
                        targetLogoY = targetY;
                        shouldConnectToLogo = true;
                    }
                } else {
                    targetLogoX = smoothedX;
                    targetLogoY = smoothedY;
                    shouldConnectToLogo = true;
                }
            } else if (isLandingPageLogoShowing) {
                // Connect to landing page logo when it's showing (currentSlide === 0)
                const logoX = logoPositionRef.current.x;
                const logoY = logoPositionRef.current.y;
                
                // Validate logo position
                if (isFinite(logoX) && isFinite(logoY) && logoX !== undefined && logoY !== undefined) {
                    targetLogoX = logoX;
                    targetLogoY = logoY;
                    shouldConnectToLogo = true;
                }
            } else {
                // No logo showing - don't clear connections immediately
                // Let them fade out naturally to prevent visual breaks
                // Only clear if both logos are inactive for multiple frames
                if (!loadingLogoPositionRef.current.active && !logoPositionRef.current.active) {
                    // Gradually fade out connections instead of clearing immediately
                    // Don't disconnect particles immediately - let animation handle fade out
                    // particlesRef.current.forEach(particle => {
                    //     if (particle && particle.isConnectedToLogo) {
                    //         particle.isConnectedToLogo = false;
                    //     }
                    // });
                    // Don't clear animations immediately - let them complete naturally
                    // logoConnectionAnimationsRef.current.clear();
                }
            }
            
            if (shouldConnectToLogo && targetLogoX !== null && targetLogoY !== null) {
                // Draw connections to logo (loading screen or landing page)
                // Same style for both: neural network connections with traveling impulses
                // Canvas must be valid
                if (!canvas || canvas.width <= 0 || canvas.height <= 0) {
                    return; // Skip if canvas is invalid
                }
                
                const logoX = targetLogoX;
                const logoY = targetLogoY;
                
                // Validate logo position is within bounds
                if (isNaN(logoX) || isNaN(logoY) || !isFinite(logoX) || !isFinite(logoY)) {
                    return; // Skip if logo position is invalid
                }
                
                // First, find all valid particles and calculate distances to logo
                // Define maxConnectionDistance outside loops so it's accessible everywhere
                const maxConnectionDistance = 600; // Maximum distance for neural connections
                const validParticles = [];
                
                particlesRef.current.forEach((particle, i) => {
                    // Skip hidden particles
                    if (particle.isHidden || particle.opacity <= 0) return;
                    
                    // Validate particle has valid position
                    if (!particle || typeof particle.x !== 'number' || typeof particle.y !== 'number' ||
                        isNaN(particle.x) || isNaN(particle.y)) {
                        return;
                    }
                    
                    const dx = logoX - particle.x;
                    const dy = logoY - particle.y;
                    const distance = Math.sqrt(dx * dx + dy * dy);
                    
                    // Connect particles within a reasonable range to the logo (neural network style)
                    if (distance < maxConnectionDistance && distance > 0) {
                        validParticles.push({ index: i, particle, distance });
                    }
                });
                
                // Sort by distance and take only the N closest (fewer on tablet)
                validParticles.sort((a, b) => a.distance - b.distance);
                const closestParticles = validParticles.slice(0, logoConnectionCount);
                const connectedIndices = new Set(closestParticles.map(p => p.index));
                
                // First, mark all particles as not connected
                particlesRef.current.forEach((particle) => {
                    if (particle) {
                        particle.isConnectedToLogo = false;
                    }
                });
                
                // Now process only the N closest particles
                closestParticles.forEach(({ index, particle, distance }) => {
                    // Mark particle as connected to logo
                    particle.isConnectedToLogo = true;
                    
                    // Create connection key
                    const connectionKey = `particle-${index}-logo`;
                    
                    // Check if this is a new connection
                    const isNewConnection = !logoConnectionAnimationsRef.current.has(connectionKey);
                    
                    // Initialize or get animation state for this connection
                    if (isNewConnection) {
                        logoConnectionAnimationsRef.current.set(connectionKey, {
                            progress: 0,
                            direction: 1, // 1 for particle -> logo, -1 for reverse
                            speed: 0.003 + Math.random() * 0.002, // Slow neural impulse speed
                            frameCreated: frameCountRef.current,
                            isNew: true
                        });
                    }
                    
                    const animState = logoConnectionAnimationsRef.current.get(connectionKey);
                    
                    // Calculate opacity based on distance (closer = brighter)
                    const distanceOpacity = (1 - distance / maxConnectionDistance) * 0.4; // Neural connection opacity
                    const baseOpacity = Math.max(0.1, distanceOpacity);
                    
                    // Update animation progress - use consistent speed regardless of frame rate
                    // This ensures smooth animation even if frame rate varies
                    const deltaTime = 1; // Normalized for 60fps
                    animState.progress += animState.speed * animState.direction * deltaTime;
                    
                    // Clamp progress to valid range and reverse direction smoothly
                    if (animState.progress >= 1) {
                        animState.progress = 1;
                        animState.direction = -1;
                    } else if (animState.progress <= 0) {
                        animState.progress = 0;
                        animState.direction = 1;
                    }
                    
                    // Ensure progress is always valid (prevent NaN or invalid values)
                    if (isNaN(animState.progress) || !isFinite(animState.progress)) {
                        animState.progress = 0;
                        animState.direction = 1;
                    }
                    
                    // Mark as no longer new after duration
                    const framesSinceCreation = frameCountRef.current - animState.frameCreated;
                    const newConnectionDuration = 300;
                    if (isNewConnection && framesSinceCreation >= newConnectionDuration) {
                        animState.isNew = false;
                    }
                    
                    // Draw neural connection line ONLY within logo bounds (for landing page logo)
                    // Get logo bounds if available
                    let logoBounds = null;
                    if (isLandingPageLogoShowing && logoPositionRef.current.width) {
                        logoBounds = {
                            left: logoPositionRef.current.left,
                            right: logoPositionRef.current.right,
                            top: logoPositionRef.current.top,
                            bottom: logoPositionRef.current.bottom
                        };
                    }
                    
                    const logoR = 243, logoG = 152, logoB = 36; // #F39824 brighter orange
                    
                    // Calculate line intersection with logo bounds
                    let lineStartX = particle.x;
                    let lineStartY = particle.y;
                    let lineEndX = logoX;
                    let lineEndY = logoY;
                    
                    // If logo bounds exist, clip line to only draw within logo area
                    if (logoBounds) {
                        // Find intersection point where line enters logo bounds
                        const dx = logoX - particle.x;
                        const dy = logoY - particle.y;
                        const length = Math.sqrt(dx * dx + dy * dy);
                        
                        if (length > 0) {
                            const unitX = dx / length;
                            const unitY = dy / length;
                            
                            // Find where line intersects logo bounds
                            let tMin = 0;
                            let tMax = 1;
                            
                            // Clip to logo bounds
                            if (Math.abs(unitX) > 0.0001) {
                                const t1 = (logoBounds.left - particle.x) / dx;
                                const t2 = (logoBounds.right - particle.x) / dx;
                                tMin = Math.max(tMin, Math.min(t1, t2));
                                tMax = Math.min(tMax, Math.max(t1, t2));
                            }
                            if (Math.abs(unitY) > 0.0001) {
                                const t1 = (logoBounds.top - particle.y) / dy;
                                const t2 = (logoBounds.bottom - particle.y) / dy;
                                tMin = Math.max(tMin, Math.min(t1, t2));
                                tMax = Math.min(tMax, Math.max(t1, t2));
                            }
                            
                            // Only draw if line intersects logo
                            if (tMin < tMax && tMax > 0) {
                                const startT = Math.max(0, tMin);
                                const endT = Math.min(1, tMax);
                                lineStartX = particle.x + dx * startT;
                                lineStartY = particle.y + dy * startT;
                                lineEndX = particle.x + dx * endT;
                                lineEndY = particle.y + dy * endT;
                            } else {
                                // Line doesn't intersect logo, skip drawing
                                return;
                            }
                        }
                    }
                    
                    // Apply global fade-in effect to logo connection lines
                    const logoLineFadeInOpacity = baseOpacity * globalFadeInRef.current;
                    
                    // Draw line with enhanced glow effect
                    const lineGradient = ctx.createLinearGradient(
                        lineStartX, lineStartY,
                        lineEndX, lineEndY
                    );
                    lineGradient.addColorStop(0, `rgba(${logoR}, ${logoG}, ${logoB}, ${logoLineFadeInOpacity * 1.2})`); // Apply fade-in
                    lineGradient.addColorStop(0.5, `rgba(${logoR + 30}, ${logoG + 20}, ${logoB + 10}, ${logoLineFadeInOpacity * 1.5})`); // Apply fade-in
                    lineGradient.addColorStop(1, `rgba(${logoR}, ${logoG}, ${logoB}, ${logoLineFadeInOpacity * 1.2})`); // Apply fade-in
                    
                    ctx.save();
                    ctx.beginPath();
                    ctx.moveTo(lineStartX, lineStartY);
                    ctx.lineTo(lineEndX, lineEndY);
                    ctx.strokeStyle = lineGradient;
                    ctx.lineWidth = 2.5; // Thicker line for better visibility
                    ctx.globalAlpha = globalFadeInRef.current; // Apply fade-in to global alpha
                    
                    // Enhanced glow effect
                    ctx.shadowBlur = 20; // Stronger glow
                    ctx.shadowColor = `rgba(${logoR}, ${logoG}, ${logoB}, ${logoLineFadeInOpacity * 1.5})`; // Apply fade-in
                    ctx.stroke();
                    
                    // Draw additional glow layers for more intense effect
                    ctx.lineWidth = 1.5;
                    ctx.shadowBlur = 15;
                    ctx.stroke();
                    
                    ctx.lineWidth = 1;
                    ctx.shadowBlur = 10;
                    ctx.stroke();
                    
                    ctx.globalAlpha = 1; // Reset global alpha
                    ctx.restore();
                    
                    // Draw traveling neural impulse (action potential) along the connection
                    // Only show impulse within logo bounds
                    // Clamp progress to ensure smooth animation
                    const glowProgress = Math.max(0, Math.min(1, animState.progress));
                    let glowX = particle.x + (logoX - particle.x) * glowProgress;
                    let glowY = particle.y + (logoY - particle.y) * glowProgress;
                    
                    // Validate glow position to prevent drawing at invalid coordinates
                    if (isNaN(glowX) || isNaN(glowY) || !isFinite(glowX) || !isFinite(glowY)) {
                        return; // Skip drawing if position is invalid
                    }
                    
                    // Clip impulse to logo bounds if available
                    if (logoBounds) {
                        // Calculate impulse position along clipped line
                        const clippedProgress = glowProgress;
                        glowX = lineStartX + (lineEndX - lineStartX) * clippedProgress;
                        glowY = lineStartY + (lineEndY - lineStartY) * clippedProgress;
                        
                        // Only draw if impulse is within logo bounds
                        if (glowX < logoBounds.left || glowX > logoBounds.right ||
                            glowY < logoBounds.top || glowY > logoBounds.bottom) {
                            return; // Skip drawing impulse outside logo
                        }
                    }
                    
                    // Neural impulse intensity (stronger in middle, visible at ends)
                    const glowIntensity = Math.sin(glowProgress * Math.PI);
                    const minIntensity = 0.5;
                    const adjustedIntensity = minIntensity + (1 - minIntensity) * glowIntensity;
                    
                    // Fade for new connections
                    let fadeFactor = 1;
                    if (isNewConnection && framesSinceCreation < newConnectionDuration) {
                        const fadeStart = newConnectionDuration * 0.7;
                        if (framesSinceCreation > fadeStart) {
                            const fadeProgress = (framesSinceCreation - fadeStart) / (newConnectionDuration - fadeStart);
                            fadeFactor = Math.max(0.7, 1 - fadeProgress * 0.3);
                        }
                    }
                    
                    // Main neural impulse (action potential traveling to logo)
                    // Apply global fade-in to logo connection impulses
                    const impulseOpacity = baseOpacity * 2.0 * adjustedIntensity * fadeFactor * globalFadeInRef.current; // Apply fade-in
                    const impulseSize = 4 + adjustedIntensity * 3;
                    
                    const coreGradient = ctx.createRadialGradient(
                        glowX, glowY, 0,
                        glowX, glowY, impulseSize
                    );
                    coreGradient.addColorStop(0, `rgba(255, 255, 240, ${impulseOpacity})`); // Apply fade-in
                    coreGradient.addColorStop(0.3, `rgba(255, 240, 200, ${impulseOpacity * 0.9})`); // Apply fade-in
                    coreGradient.addColorStop(0.6, `rgba(255, 220, 160, ${impulseOpacity * 0.6})`); // Apply fade-in
                    coreGradient.addColorStop(1, 'rgba(255, 180, 100, 0)');
                    
                    ctx.fillStyle = coreGradient;
                    ctx.beginPath();
                    ctx.arc(glowX, glowY, impulseSize, 0, Math.PI * 2);
                    ctx.fill();
                    
                    // Bright white-hot core (electrical charge)
                    ctx.fillStyle = `rgba(255, 255, 255, ${impulseOpacity * 0.9})`;
                    ctx.beginPath();
                    ctx.arc(glowX, glowY, impulseSize * 0.4, 0, Math.PI * 2);
                    ctx.shadowBlur = 15;
                    ctx.shadowColor = `rgba(255, 240, 200, ${impulseOpacity})`;
                    ctx.fill();
                    ctx.shadowBlur = 0;
                    
                    // Draw trailing effect (depolarization wave)
                    if (glowProgress > 0.1) {
                        const trailProgress = Math.max(0, Math.min(1, glowProgress - 0.2));
                        const trailX = particle.x + (logoX - particle.x) * trailProgress;
                        const trailY = particle.y + (logoY - particle.y) * trailProgress;
                        
                        // Validate trail position
                        if (isNaN(trailX) || isNaN(trailY) || !isFinite(trailX) || !isFinite(trailY)) {
                            return; // Skip drawing if position is invalid
                        }
                        
                        const trailOpacity = baseOpacity * 1.0 * fadeFactor * globalFadeInRef.current * (1 - (glowProgress - trailProgress) / 0.2); // Apply fade-in
                        const trailGradient = ctx.createRadialGradient(
                            trailX, trailY, 0,
                            trailX, trailY, impulseSize * 0.8
                        );
                        trailGradient.addColorStop(0, `rgba(255, 200, 140, ${trailOpacity * 0.6})`);
                        trailGradient.addColorStop(1, 'rgba(255, 180, 100, 0)');
                        
                        ctx.fillStyle = trailGradient;
                        ctx.beginPath();
                        ctx.arc(trailX, trailY, impulseSize * 0.8, 0, Math.PI * 2);
                        ctx.fill();
                    }
                });
                
                // Clean up logo connections for particles NOT in the top 10 closest
                logoConnectionAnimationsRef.current.forEach((animState, key) => {
                    const particleIndex = parseInt(key.split('-')[1]);
                    // If this particle is not in the connected indices (top 10), remove its connection
                    if (!connectedIndices.has(particleIndex)) {
                        const particle = particlesRef.current[particleIndex];
                        if (particle) {
                            particle.isConnectedToLogo = false; // Mark as not connected
                        }
                        logoConnectionAnimationsRef.current.delete(key);
                    }
                });
            } else {
                // Clear all logo connections when:
                // - Not on loading screen
                // - Logo is not visible (landing page logo not showing or loading screen logo not active)
                // - Any other condition where connections shouldn't exist
                particlesRef.current.forEach(particle => {
                    if (particle) {
                        particle.isConnectedToLogo = false;
                    }
                });
                logoConnectionAnimationsRef.current.clear();
                loadingLogoPositionRef.current.active = false; // Ensure it's marked as inactive
            }
            
            // FINAL SAFETY CHECK: Only allow connections to landing page logo when currentSlide === 0
            // This is a redundant check to ensure connections are only active when logo is showing
            const currentSlideValueFinal = currentSlideRef.current;
            if (logoPositionRef.current.active && !isOnLoadingScreen && currentSlideValueFinal !== 0) {
                // Landing page logo exists but is not showing (different slide) - force clear all logo connections
                logoConnectionAnimationsRef.current.clear();
                // Mark all particles as not connected
                particlesRef.current.forEach(particle => {
                    if (particle) {
                        particle.isConnectedToLogo = false;
                    }
                });
                // Also ensure loading logo is marked inactive
                loadingLogoPositionRef.current.active = false;
            }

            animationFrameRef.current = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            window.removeEventListener('resize', resizeCanvas);
            window.removeEventListener('resize', updateFooterPosition);
            window.removeEventListener('resize', updatePeopleCardRects);
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseleave', handleMouseLeave);
            if (peopleObserverRef.current) {
                peopleObserverRef.current.disconnect();
            }
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
            spawnCancelledRef.current = true;
        };
    }, [isPhone, isTablet]);

    // Update currentSlide ref whenever it changes
    useEffect(() => {
        currentSlideRef.current = currentSlide;
    }, [currentSlide]);

    return (
        <canvas
            ref={canvasRef}
            className="fixed inset-0 pointer-events-none"
            style={{
                zIndex: 1,
                mixBlendMode: 'screen',
                opacity: 0.7,
            }}
        />
    );
};

export default EnhancedBackground;

